import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { VenueMapContentProps } from 'src/components/Events/VenueMap/VenueMapProps';
import { ErrorTypes } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { useAutoCompMapSectionHandler } from 'src/hooks/useAutoCompMapSectionHandler';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { ChevronLeftIcon, ChevronRightIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Event,
  SectionSectionGroup,
  VenueMapZoneConfigInfo,
  VenueZoneConfigOverride,
  VenueZoneConfigType,
} from 'src/WebApiController';

import { SeatMapCard } from '../../Sections/SeatMapCard';
import { EventVenueMapFormType } from '../EventVenueMapModal';
import * as styles from './EventVenueMapSelector.css';

export const EventVenueMapSelector = ({
  event,
  selectedOverrideId,
  setSelectedOverrideId,
  selectedConfigType,
  setSelectedConfigType,
  createGetColor,
  venueZoneMapConfigInfoQuery,
  venueZoneMapConfigOverrideInfoQuery,
  setVenueZoneConfigTypeOverrideMutation,
}: {
  event: Event;
  selectedOverrideId: number | null;
  setSelectedOverrideId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedConfigType: VenueZoneConfigType | null;
  setSelectedConfigType: React.Dispatch<
    React.SetStateAction<VenueZoneConfigType | null>
  >;
  createGetColor: (
    sectionSectionGroupLookup?: {
      [key: string]: SectionSectionGroup;
    } | null
  ) => NonNullable<VenueMapContentProps['getColor']>;
  venueZoneMapConfigInfoQuery: UseQueryResult<
    VenueMapZoneConfigInfo | null,
    Error
  >;
  venueZoneMapConfigOverrideInfoQuery: UseQueryResult<
    {
      [key: string]: VenueZoneConfigOverride;
    } | null,
    Error
  >;
  setVenueZoneConfigTypeOverrideMutation: UseMutationResult<
    void,
    ErrorTypes,
    {
      eventId?: number | null;
      venueZoneOverrideId?: number | null;
      venueZoneConfigType?: VenueZoneConfigType | null;
      setMapAsDefault?: boolean | null;
    },
    unknown
  >;
}) => {
  const changeSectionGroupDialog = useBasicDialog();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  const onLaunchingSingleSectionDialog = useCallback(
    () => changeSectionGroupDialog.launchDialog(),
    [changeSectionGroupDialog]
  );

  const { watch } = useFormContext<EventVenueMapFormType>();
  const setMapAsDefault = watch('setMapAsDefault');

  const { onResetSections } = useAutoCompMapSectionHandler(
    onLaunchingSingleSectionDialog
  );

  // Check scroll position and update indicators
  const checkScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const isScrollable = scrollWidth > clientWidth;
    const isAtStart = scrollLeft <= 0;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    setShowLeftArrow(isScrollable && !isAtStart);
    setShowRightArrow(isScrollable && !isAtEnd);
    setShowLeftGradient(isScrollable && !isAtStart);
    setShowRightGradient(isScrollable && !isAtEnd);
  }, []);

  // Handle scroll with arrows
  const scrollToDirection = useCallback((direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;

    const scrollAmount = 260;
    const currentScroll = scrollContainerRef.current.scrollLeft;
    const newScrollPosition =
      direction === 'left'
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;

    scrollContainerRef.current.scrollTo({
      left: newScrollPosition,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();

    container.addEventListener('scroll', checkScrollPosition);

    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [checkScrollPosition, venueZoneMapConfigOverrideInfoQuery.data]);

  const onVenueZoneMapClick = useCallback(
    (
      venueZoneOverrideId: number | null,
      venueZoneConfigType: VenueZoneConfigType | null
    ) => {
      setVenueZoneConfigTypeOverrideMutation.mutate({
        eventId: event!.viagId,
        venueZoneOverrideId,
        venueZoneConfigType,
        setMapAsDefault: setMapAsDefault ?? undefined,
      });
      setSelectedOverrideId(venueZoneOverrideId);
      setSelectedConfigType(venueZoneConfigType);
      onResetSections();
    },
    [
      setVenueZoneConfigTypeOverrideMutation,
      event,
      setMapAsDefault,
      setSelectedOverrideId,
      setSelectedConfigType,
      onResetSections,
    ]
  );

  const disabledButtons = setVenueZoneConfigTypeOverrideMutation.isPending;

  return (
    <div className={styles.scrollWrapper}>
      <div
        className={clsx(
          styles.scrollIndicatorLeft,
          showLeftGradient && styles.scrollIndicatorVisible
        )}
      />

      <button
        className={clsx(
          styles.navigationArrowLeft,
          showLeftArrow
            ? styles.navigationArrowVisible
            : styles.navigationArrowHidden
        )}
        onClick={() => scrollToDirection('left')}
        aria-label="Scroll left"
      >
        <ChevronLeftIcon size={vars.iconSize.m} />
      </button>

      <div
        className={clsx(
          styles.scrollIndicatorRight,
          showRightGradient && styles.scrollIndicatorVisible
        )}
      />

      <button
        className={clsx(
          styles.navigationArrowRight,
          showRightArrow
            ? styles.navigationArrowVisible
            : styles.navigationArrowHidden
        )}
        onClick={() => scrollToDirection('right')}
        aria-label="Scroll right"
      >
        <ChevronRightIcon size={vars.iconSize.m} />
      </button>

      <div ref={scrollContainerRef} className={styles.venueMapCardsContainer}>
        <div className={styles.seatMapContainer}>
          <SeatMapCard
            isInteractive={!disabledButtons}
            isSmall={true}
            isSelected={
              selectedOverrideId == null &&
              selectedConfigType === VenueZoneConfigType.Amphitheater
            }
            onClick={() => {
              onVenueZoneMapClick(null, VenueZoneConfigType.Amphitheater);
            }}
            footer={<Content id={ContentId.Amphitheater} />}
          >
            <EventVenueMap
              isZoomEnabled={false}
              getColor={createGetColor(
                venueZoneMapConfigInfoQuery?.data?.venueMapZoneConfigs
                  .Amphitheater
              )}
            />
          </SeatMapCard>
        </div>

        <div className={styles.seatMapContainer}>
          <SeatMapCard
            isInteractive={!disabledButtons}
            isSmall={true}
            isSelected={
              selectedOverrideId == null &&
              selectedConfigType ===
                VenueZoneConfigType.ConcertsSectionsBehindStage
            }
            onClick={() => {
              onVenueZoneMapClick(
                null,
                VenueZoneConfigType.ConcertsSectionsBehindStage
              );
            }}
            footer={<Content id={ContentId.ConcertsSectionsBehindStage} />}
          >
            <EventVenueMap
              isZoomEnabled={false}
              getColor={createGetColor(
                venueZoneMapConfigInfoQuery?.data?.venueMapZoneConfigs
                  .ConcertsSectionsBehindStage
              )}
            />
          </SeatMapCard>
        </div>

        <div className={styles.seatMapContainer}>
          <SeatMapCard
            isInteractive={!disabledButtons}
            isSmall={true}
            isSelected={
              selectedOverrideId == null &&
              selectedConfigType === VenueZoneConfigType.SportsOrConcertsWith360
            }
            onClick={() => {
              onVenueZoneMapClick(
                null,
                VenueZoneConfigType.SportsOrConcertsWith360
              );
            }}
            footer={<Content id={ContentId.SportsOrConcertsWith360} />}
          >
            <EventVenueMap
              isZoomEnabled={false}
              getColor={createGetColor(
                venueZoneMapConfigInfoQuery?.data?.venueMapZoneConfigs
                  .SportsOrConcertsWith360
              )}
            />
          </SeatMapCard>
        </div>

        {Object.entries(venueZoneMapConfigOverrideInfoQuery.data ?? {}).map(
          ([k, v], idx) => {
            return (
              <div key={idx} className={styles.seatMapContainer}>
                <SeatMapCard
                  isInteractive={!disabledButtons}
                  isSmall={true}
                  isSelected={selectedOverrideId == Number.parseInt(k)}
                  onClick={() => {
                    onVenueZoneMapClick(Number.parseInt(k), null);
                  }}
                  footer={v.name}
                >
                  <EventVenueMap
                    isZoomEnabled={false}
                    getColor={createGetColor(v.sectionSectionGroupLookup)}
                  />
                </SeatMapCard>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};
